/**
 * TimerRing - SVG circular progress countdown for the Pomodoro timer.
 *
 * Renders a large (240px) circular progress ring that depletes as time runs out.
 * Center displays remaining time in mm:ss format. Ring color changes based on
 * the current Pomodoro phase.
 *
 * @see Requirements 7.1, 7.2
 */

import type { PomodoroPhase } from '@/stores/pomodoroStore';

export interface TimerRingProps {
  /** Remaining seconds on the timer. */
  remainingSeconds: number;
  /** Total seconds for the current phase (used for progress calculation). */
  totalSeconds: number;
  /** Current Pomodoro phase — determines ring color. */
  phase: PomodoroPhase;
}

// ─── Phase Colors ────────────────────────────────────────────────────────────

/** Phase-specific ring colors per design system. */
const PHASE_COLORS: Record<Exclude<PomodoroPhase, 'idle'>, string> = {
  work: 'var(--color-primary)',      // Primary pink (#E8A0BF)
  shortBreak: 'var(--color-pomodoro-break)', // Soft mint
  longBreak: 'var(--color-secondary)', // Lavender (#B4A7D6)
};

// ─── Helpers ─────────────────────────────────────────────────────────────────

/** Format seconds into mm:ss display string. */
function formatTime(seconds: number): string {
  const clamped = Math.max(0, seconds);
  const mins = Math.floor(clamped / 60);
  const secs = clamped % 60;
  return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
}

// ─── Component ───────────────────────────────────────────────────────────────

const SIZE = 240;
const STROKE_WIDTH = 12;

export function TimerRing({ remainingSeconds, totalSeconds, phase }: TimerRingProps) {
  const radius = (SIZE - STROKE_WIDTH) / 2;
  const circumference = 2 * Math.PI * radius;

  // Progress: 100% at start (full ring), decreases to 0% when timer ends
  const progress = totalSeconds > 0 ? remainingSeconds / totalSeconds : 0;
  const clampedProgress = Math.min(1, Math.max(0, progress));
  const strokeDashoffset = circumference * (1 - clampedProgress);

  // Ring color based on phase (idle falls back to primary)
  const ringColor = phase === 'idle'
    ? 'var(--color-primary)'
    : PHASE_COLORS[phase];

  const timeDisplay = formatTime(remainingSeconds);
  const ariaLabel = `Timer: ${timeDisplay} remaining`;

  return (
    <div
      className="relative inline-flex items-center justify-center"
      style={{ width: SIZE, height: SIZE }}
      role="timer"
      aria-label={ariaLabel}
      aria-live="off"
    >
      <svg
        width={SIZE}
        height={SIZE}
        className="transform -rotate-90"
        aria-hidden="true"
      >
        {/* Background track circle */}
        <circle
          cx={SIZE / 2}
          cy={SIZE / 2}
          r={radius}
          fill="none"
          stroke="var(--color-border)"
          strokeWidth={STROKE_WIDTH}
          opacity={0.5}
        />
        {/* Progress arc */}
        <circle
          cx={SIZE / 2}
          cy={SIZE / 2}
          r={radius}
          fill="none"
          stroke={ringColor}
          strokeWidth={STROKE_WIDTH}
          strokeLinecap="round"
          strokeDasharray={circumference}
          strokeDashoffset={strokeDashoffset}
          className="transition-[stroke-dashoffset] duration-default ease-ease-default"
        />
      </svg>

      {/* Center time display */}
      <span
        className="absolute inset-0 flex items-center justify-center text-text-heading font-bold pointer-events-none tabular-nums"
        style={{ fontSize: '3rem', letterSpacing: '-0.02em' }}
      >
        {timeDisplay}
      </span>
    </div>
  );
}

TimerRing.displayName = 'TimerRing';
